import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowDownLeft, ArrowUpRight, ExternalLink, EyeOff, Undo2, Trash2 } from "lucide-react";
import { mailboxApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/components/SimpleToast";
import { AssignPopover } from "./AssignPopover";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");
const METHOD = { email: "per Adresse", domain: "per Domain", rule: "per Regel", manual: "manuell" };
const targetUrl = (e) => (e.target_type === "vendor" ? `/medical-software/${e.target_id}` : `/customers/${e.target_id}`);

export function EmailRow({ email: e, onChanged, showTarget = false }) {
  const { user } = useAuth();
  const toast = useToast();
  const canEdit = user?.role !== "readonly";
  const sent = e.folder === "sent";
  const people = sent ? (e.to || []).map((r) => r.name || r.email).join(", ") : e.from_name || e.from_email;

  const act = async (fn, msg) => {
    try { await fn(e.id); toast.success(msg); onChanged?.(); } catch { toast.error("Aktion fehlgeschlagen"); }
  };

  return (
    <Card data-testid={`mail-row-${e.id}`}>
      <CardContent className="py-3 flex flex-wrap items-start gap-3">
        <div className={`mt-0.5 w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${sent ? "bg-blue-50 text-blue-600" : "bg-emerald-50 text-emerald-600"}`} title={sent ? "Gesendet" : "Eingang"}>
          {sent ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownLeft className="w-4 h-4" />}
        </div>
        <div className="min-w-0 flex-1 basis-64 space-y-0.5">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="font-medium truncate max-w-full">{e.subject}</span>
            <span className="text-xs text-muted-foreground">{fmt(e.received_at)}</span>
          </div>
          <p className="text-sm text-muted-foreground truncate">{sent ? "An: " : "Von: "}{people}{!sent && e.from_name ? ` <${e.from_email}>` : ""}</p>
          {e.excerpt && <p className="text-sm line-clamp-2">{e.excerpt}</p>}
          {showTarget && e.status === "assigned" && (
            <p className="text-xs pt-0.5">
              <Link to={targetUrl(e)} className="text-primary hover:underline" data-testid={`mail-target-${e.id}`}>{e.target_name}</Link>
              <span className="text-muted-foreground"> · {METHOD[e.match_method] || "zugeordnet"}</span>
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-1 w-full sm:w-auto shrink-0">
          {e.web_link && (
            <Button variant="ghost" size="icon" title="Originalmail in Outlook öffnen" onClick={() => window.open(e.web_link, "_blank", "noopener,noreferrer")} data-testid={`mail-open-${e.id}`}><ExternalLink className="w-4 h-4" /></Button>
          )}
          {canEdit && e.status === "open" && <AssignPopover email={e} onAssigned={onChanged} />}
          {canEdit && e.status === "open" && <Button variant="outline" size="sm" onClick={() => act(mailboxApi.ignore, "Als ignoriert markiert")} data-testid={`mail-ignore-${e.id}`}><EyeOff className="w-4 h-4 mr-1.5" />Ignorieren</Button>}
          {canEdit && e.status !== "open" && <Button variant="ghost" size="sm" onClick={() => act(mailboxApi.reopen, "Zurück in „Zuordnung offen“")} data-testid={`mail-reopen-${e.id}`}><Undo2 className="w-4 h-4 mr-1.5" />{e.status === "ignored" ? "Wiederherstellen" : "Zuordnung lösen"}</Button>}
          {user?.role === "admin" && <Button variant="ghost" size="icon" title="Löschen (nur Admin)" onClick={() => window.confirm("Diese E-Mail aus dem CRM löschen? Die Originalmail in Exchange bleibt erhalten.") && act(mailboxApi.remove, "E-Mail gelöscht")} data-testid={`mail-delete-${e.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>}
        </div>
        {e.status === "ignored" && <Badge variant="outline" className="w-full sm:w-auto">Ignoriert</Badge>}
      </CardContent>
    </Card>
  );
}
